/* eslint-disable complexity */
import { ElementRef, Renderer2, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { SwiperOptions } from 'swiper';

import {
  dotCmsBlogSlugRegex,
  dotCmsHomepageSlugRegex,
  dotCmsPageSlugRegex,
  dotCmsRouterUrlRegex,
} from '../configurations/dot-cms-regex';
import { DotCmsUveFacade } from '../facades/dot-cms-uve.facade';
import {
  GlobalCarouselContentlet,
  IcmCarousalContentlet,
  VideoContentlet,
} from '../models/dot-cms-uve-contentlet.model';

//Todo: Remove this function
export function isRouterLink(url: string) {
  return url.substring(0, 4) === 'http' ? false : true;
}

export const isSiteEmbedded = () => typeof window !== 'undefined' && window.top && window.top !== window;

export const emitDotCmsEMAEvent = (pathname: string) => {
  const customEvent = new CustomEvent('ng-event', {
    bubbles: false,
    cancelable: false,
    detail: {
      name: 'remote-render-edit',
      data: { pathname },
    },
  });

  window.top.document.dispatchEvent(customEvent);
};

export const generateDotCmsRouterURI = (uri: string) => {
  const match = uri.match(dotCmsRouterUrlRegex);
  const updatedURI = match ? `/${match[2]}` : uri;
  const indexURI = uri.split('?')[1] ? `/index?${uri.split('?')[1]}` : '/index';
  return dotCmsHomepageSlugRegex.test(updatedURI) ? indexURI : updatedURI;
};
export const getDotCmsPageURI = (uri: string) => (dotCmsPageSlugRegex.test(uri) ? uri.split('/page')[1] : uri);

export const isDotCmsPageURI = (uri: string) =>
  dotCmsPageSlugRegex.test(uri) || dotCmsBlogSlugRegex.test(uri) || dotCmsHomepageSlugRegex.test(uri);

export function isDotcmsSaticPage(href: string): boolean {
  const hrefSplit = href.split('/');

  const index = hrefSplit.indexOf('page');

  if (index !== -1 && index < hrefSplit.length - 1) {
    return true;
  }

  return false;
}
export function isHomePage(href: string): boolean {
  const regex = /^(home|.*\/home)$/;
  if (regex.test(href)) {
    return true;
  }
  return false;
}
export function isDotcmsPage(href: string) {
  if (isHomePage(href) || isDotcmsSaticPage(href)) {
    return true;
  }
  return false;
}

export function getDotCmsPageUrl(href: string) {
  if (isHomePage(href)) {
    return '/index';
  }

  const hrefSplit = href.split('/');

  const index = hrefSplit.indexOf('page');

  if (isDotcmsSaticPage(href)) {
    const subpagePart = hrefSplit.slice(index + 1).join('/');
    return `/${subpagePart}`;
  }
}

export function emitDotCmsEvent(event: Event, url: string) {
  if (isSiteEmbedded()) {
    const hrefValue = url === undefined ? (event.target as HTMLAnchorElement).href : url;

    if (!hrefValue) {
      return true;
    }
    const dotcmsPageUrl = getDotCmsPageUrl(hrefValue);
    if (dotcmsPageUrl) {
      emitDotCmsEMAEvent(dotcmsPageUrl);
    }
  }
}
//@ts-ignore
export const sliceContents = (noOfItems: number, contents: T[]) => {
  //@ts-ignore
  let carouselContents: T[] = [];
  carouselContents =
    noOfItems <= contents.length - 1 ? (noOfItems === 0 ? contents : contents.slice(0, noOfItems)) : contents;
  return carouselContents;
};

export function configureSwiper(
  contentlet: IcmCarousalContentlet | GlobalCarouselContentlet,
  [smallWidth, mediumWidth, largeWidth]: number[]
): SwiperOptions {
  return {
    loop: (contentlet.loop as boolean) || false,
    //@ts-ignore
    speed: ((contentlet.speed ?? contentlet.speed) as number) || 300,
    pagination: (contentlet.showIndicator as string[]).length ? { clickable: true } : false,
    autoplay:
      //@ts-ignore
      contentlet.autoPlay
        ? {
            //@ts-ignore
            delay: (contentlet.autoPlay as number) * 1000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    //@ts-ignore
    breakpoints: configureSlides(+contentlet.slideItems, [smallWidth, mediumWidth, largeWidth]),
  };
}

export function configureSlides(slideItems: number, [smallWidth, mediumWidth, largeWidth]: number[]) {
  switch (slideItems) {
    case 1: {
      return {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
        },
      };
    }
    case 2: {
      return {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
        },
        [smallWidth]: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
      };
    }
    case 3: {
      return {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
        },
        [smallWidth]: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
        [mediumWidth]: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
      };
    }
    default: {
      return {
        0: {
          slidesPerView: 1,
          slidesPerGroup: 1,
        },
        [smallWidth]: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
        [mediumWidth]: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
        [largeWidth]: {
          slidesPerView: 4,
          slidesPerGroup: 4,
        },
      };
    }
  }
}

export function filterEmaDotcmsPageLinks(elementRef: ElementRef, renderer: Renderer2) {
  if (isSiteEmbedded()) {
    const anchorTags = elementRef.nativeElement.querySelectorAll('a');
    if (anchorTags) {
      anchorTags.forEach((anchor: HTMLAnchorElement) => {
        let href = anchor.getAttribute('href');
        if (href && isRouterLink(href) && href.substring(0, 1) !== '/') {
          href = `/${href}`;
        }
        if (href && isDotcmsPage(href)) {
          renderer.setAttribute(anchor, 'data-href-url', href);
          renderer.removeAttribute(anchor, 'href');
        }
      });
    }
  }
}

export function onClickEmaDotcmsPageLinks(elementRef: ElementRef, renderer: Renderer2) {
  if (isSiteEmbedded()) {
    renderer.listen(elementRef.nativeElement, 'click', event => {
      const url = (event.target as HTMLElement).getAttribute('data-href-url');
      if (url) {
        emitDotCmsEvent(event, url);
      }
    });
  }
}

//Used to Remove protocols from give URL
export function truncateProtocol(url: string) {
  const httpProtocol = 'http://';
  const httpsProtocol = 'https://';

  if (url.startsWith(httpProtocol)) {
    return url.slice(httpProtocol.length);
  } else if (url.startsWith(httpsProtocol)) {
    return url.slice(httpsProtocol.length);
  } else if (url.startsWith('/')) {
    return url.slice(1);
  } else {
    return url; // URL does not start with http:// or https://, return as is
  }
}

export function dotCMSNavigationLink(url: string) {
  return url === '/index' ? 'home' : `/page/${truncateProtocol(url)}`;
}
//@ts-ignore
export function isIndexPagePresent(folderName: T): boolean {
  if (folderName?.children) {
    //@ts-ignore
    return folderName.children.some(child => child.href.split('/').includes('index'));
  }
  return false;
}

//tryProcessVideo used to process the video URL based on content type.

export function tryProcessVideo(contentlet?: VideoContentlet, _sanitizer?: DomSanitizer): SafeUrl {
  const youTubeVideoRegex =
    /(?:youtube\.com\/\S*(?:(?:\/e(?:mbed))?\/|watch\?(?:\S*?&?v\=))|youtu\.be\/)([a-zA-Z0-9_-]{6,11})/i;
  const vimeoVideoRegex =
    /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|)(\d+)(?:|\/\?)/i;
  if (contentlet.contentType === 'Video1') {
    if (contentlet.url?.match(youTubeVideoRegex)) {
      const videoId = contentlet.url?.match(youTubeVideoRegex)[1];
      const videoUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
      return _sanitizer.bypassSecurityTrustResourceUrl(videoUrl.toString());
    }
    return;
  }
  if (contentlet.contentType === 'Video') {
    if (contentlet.url.match(youTubeVideoRegex)) {
      const videoId = contentlet.url.match(youTubeVideoRegex)[1];
      const videoUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
      if (contentlet.autoPlayVideo) {
        videoUrl.searchParams.set('autoplay', '1');
      }
      if (contentlet.mute) {
        videoUrl.searchParams.set('mute', '1');
      }

      return _sanitizer.bypassSecurityTrustResourceUrl(videoUrl.toString());
    }
    return;
  }
  if (contentlet.contentType === 'VideoVimeo') {
    if (contentlet.url.match(vimeoVideoRegex)) {
      const videoId = contentlet.url.match(vimeoVideoRegex)[4];
      const videoUrl = new URL(`https://player.vimeo.com/video/${videoId}`);
      if (contentlet.autoPlayVideo) {
        videoUrl.searchParams.set('autoplay', '1');
      }
      if (contentlet.mute) {
        videoUrl.searchParams.set('muted', '1');
      }
      return _sanitizer.bypassSecurityTrustResourceUrl(videoUrl.toString());
    }
    return;
  }
}

export function managePopup(id: string, property: string) {
  const elements = document.getElementsByClassName(id);
  // Loop through each element and change properties
  Array.from(elements).forEach((element: HTMLElement) => {
    if (property === 'block') {
      element.classList.add('show-popup');
    } else {
      element.classList.remove('show-popup');
    }
  });
}

export function isInsideCarousel(element: HTMLElement): boolean {
  let parent = element.parentElement;

  while (parent) {
    if (
      parent.tagName.toLowerCase() === 'custom-dot-cms-carousel' ||
      parent.tagName.toLowerCase() === 'custom-dot-cms-global-carousel'
    ) {
      return true;
    }
    parent = parent.parentElement;
  }

  return false;
}

export function navigateTo(url: string) {
  const dotCmsFacade = inject(DotCmsUveFacade);
  return dotCmsFacade.navigateTo(url);
}
